import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { mq } from '../../../common/src/utils/styledComponentsUtils';
import { useHistory, useLocation, useParams } from 'react-router';
import cogoToast from 'cogo-toast';
import { parse, stringify } from 'query-string';
import { useLouiseDispatch } from '../store/redux/store';

// Components
import SelectionContainer from '../components/selection/SelectionContainer';
import LoadingOverlay from '../../../common/src/components/LoadingOverlay';

// Data
import DefaultSelection from '../constants/DefaultSelection.json';

// Redux
import { useSelector } from 'react-redux';

import {
  setSelectionData,
  setGuarantees,
  setCompanies,
  persistSelectionState,
  loadSelectionState,
  setParty,
} from '../store/redux/slices/selectionSlice';

// Services
import { formatErrorMessage } from '../../../common/src/legacy/services/errorMessageService';
import { retrieveProFlowGuarantees } from '../../../common/src/legacy/services/apiRouterService';

// Types
import { RootState } from '../store/redux/reducers/rootReducer';
import { GuaranteesResponse } from '../../../../types/Quotes';
import {
  Company,
} from '../../../../types/ProductFactory';
import useFeatureFlags from '../../../app/src/hooks/useFeatureFlags';
// import { isStaging } from '../../../common/src/utils/globalVariables';
import {
  useAffiliations,
  useBrokerAffiliations,
} from '../components/selection/SelectionQueries';
import { flowIdSalesConversation } from '../../../flows/src/constants/flowIds';
import useSalesConversationFlowPayload from '../hooks/useSalesConversationFlowPayload';
import useUuid from '../../../common/src/hooks/useUuid';
import useCampaignsOutputUrl from '../hooks/useCampaignsOutputUrl';
import usePartyId from '../../../common/src/hooks/query-params/usePartyId';
import useIsNLplan from '../../../common/src/hooks/useIsNLPlan';
import useSessionId from '../../../flows/src/hooks/useSessionId';
import useFlowsInitNew from '../../../flows/src/hooks/init/useFlowsInitNew';
import {
  getDisabledCompanies,
  isCompanyDisabled,
} from '../../../common/src/api/getDisabledCompanies';
import { isSocialLoginUrl } from '../../../app/src/features/authentication/constants/SocialAuth';

export interface SelectedItem {
  [x: string]: string[] | { [x: string]: string[] };
}

// Temporary
const PIA = {
  affiliation_info: {
    can_save: [],
    insurances: ['car'],
    label: 'PIA',
    logo: 'https://files.wegroup.be/logos/insurances/pia.png',
    name: 'pia',
    needs_access_request: false,
    needs_verification: false,
    required_integrations: [],
    required_nrs: ['nr'],
  },
  disabled: false,
};

const SelectionView: React.FC<React.PropsWithChildren<unknown>> = () => {
  const rejectedAffiliations = ['fidea'];

  const history = useHistory();
  const location = useLocation<{ last?: boolean; riskObjects?: string[] }>();
  const sessionActive = useSelector(
    (state: RootState) => state.productFactory.sessionActive,
  );

  const selectionTypes = useSelector(
    (state: RootState) => state.selection.selectionTypes,
  );
  const selectedRiskObjects = useSelector(
    (state: RootState) => state.selection.selectedRiskObjects,
  );
  const selectedCompanies = useSelector(
    (state: RootState) => state.selection.selectedCompanies,
  );

  const { isNL } = useIsNLplan();

  const sessionId = useSessionId();
  const page = parseInt(useParams<{ position: string }>().position);

  const louiseDispatch = useLouiseDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const featureFlags = useFeatureFlags();

  const { getFlowPayload } = useSalesConversationFlowPayload();
  const { initFlow } = useFlowsInitNew();
  const campaignOutputUrl = useCampaignsOutputUrl();
  const partyId = usePartyId();

  const uuid = useUuid();

  const { data: affiliations, isLoading: isAffiliationsLoading } =
    useAffiliations();
  const { data: brokerAffiliations, isLoading: isBrokerAffiliationsLoading } =
    useBrokerAffiliations();

  useEffect(() => {
    (async () => {
      if (sessionId && typeof sessionId === 'string') {
        await louiseDispatch(loadSelectionState(sessionId));
      }
      if (partyId) louiseDispatch(setParty(partyId));
      initial();
    })();
  }, []);

  const nextSelection = async (current: number) => {
    if (sessionId && typeof sessionId === 'string')
      await louiseDispatch(persistSelectionState(sessionId));
    if (isSocialLoginUrl) {
      await louiseDispatch(persistSelectionState(uuid));
      const sessionId = await initFlow({
        flowId: flowIdSalesConversation,
        inputUrl: `${location.pathname}`,
        outputUrl: `${campaignOutputUrl}`,
        payload: await getFlowPayload(uuid),
      });
      if (sessionId) await louiseDispatch(persistSelectionState(sessionId));
      return;
    }
    if (selectionTypes[current].type === 'riskObjects') await getCompanies();
    if (selectionTypes[current].type === 'companies') await getGuarantees();
    if (current >= Object.keys(selectionTypes).length - 1) {
      await louiseDispatch(persistSelectionState(uuid));
      const sessionId = await initFlow({
        flowId: flowIdSalesConversation,
        inputUrl: `${location.pathname}`,
        outputUrl: `/product/session/finalize`,
        payload: await getFlowPayload(uuid),
      });
      if (sessionId) await louiseDispatch(persistSelectionState(sessionId));
      return;
    }
    history.push({
      pathname: (current + 1).toString(),
      search: location.search,
    });
  };

  const previousSelection = (current: number) => {
    if (isSocialLoginUrl) return history.push('/product/session');
    if (current === 0) return history.push('/conversation');
    if (current === 0) return history.push('/dashboard');
    history.push({
      pathname: (current - 1).toString(),
      search: location.search,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const retrieveDisabledCompanies = () => {
    const resp = getDisabledCompanies();

    // Make sure the values are mapped to lower case to avoid errors
    return Object.assign(
      {},
      ...Object.entries(resp)
        .filter(([key]) => {
          // Overwrite Euromex disable logic based on feature flag
          return key !== 'euromex';
        })
        .map(([key, value]) => ({
          [key?.toLowerCase()]: value,
        })),
    );
  };

  const getCompanies = async () => {
    if (!affiliations || !brokerAffiliations) return;
    setIsLoading(true);
    const affiliationsCopy = { ...affiliations };

    if (featureFlags?.isPiaEnabled) {
      affiliationsCopy.items.push(PIA);
    }
    const disabledCompanies = await retrieveDisabledCompanies();

    const companies: Company[] = affiliationsCopy.items
      .filter(({ affiliation_info: { name } }) => {
        return (
          brokerAffiliations.items
            .map(({ affiliation }) => affiliation.name)
            .includes(name) && !rejectedAffiliations.includes(name)
        );
      })
      .filter(
        ({ affiliation_info: { insurances } }) =>
          !insurances.every(
            (insurance) => !selectedRiskObjects.includes(insurance),
          ),
      )
      .map(({ affiliation_info: { name, label, logo, insurances } }) => ({
        name,
        label,
        logo,
        insurances,
        disabledObj: isCompanyDisabled(name, disabledCompanies),
      }));

    louiseDispatch(setCompanies(companies));
    setIsLoading(false);
  };

  /**
   * If companies are not a selection view then the guarantees for
   * the risk objects can be retrieved.
   */
  const getGuarantees = async () => {
    setIsLoading(true);

    const [resp, status] = await retrieveProFlowGuarantees(
      stringify({
        company: selectedCompanies,
        insurance_type: selectedRiskObjects,
      }),
      isNL ? 'NL' : 'BE',
    );

    if (status > 201) {
      const { detail } = formatErrorMessage(
        resp?.msg,
        resp?.detail,
        resp?.error,
      );
      return cogoToast.error(detail, { hideAfter: 15 });
    }

    louiseDispatch(setGuarantees((resp as GuaranteesResponse).tags));
    setIsLoading(false);
  };

  const initial = () => {
    if (isSocialLoginUrl && selectedCompanies.length === 0)
      return history.push('/product/session');

    const selectionDataVar = JSON.parse(
      JSON.stringify(DefaultSelection),
    ) as typeof DefaultSelection;
    const { risk_object } = parse(location.search);

    if (location?.state?.last) {
      history.replace({
        pathname: `/product/session/selection/${Math.max(
          0,
          Object.keys(selectionTypes).length - 1,
        )}`,
        search: location.search,
      });
    }
    if (isSocialLoginUrl && risk_object) {
      selectionDataVar.riskObjects.items =
        selectionDataVar.riskObjects.items.filter(({ name }) =>
          risk_object?.includes(name),
        );
    } else if (page == null || isNaN(page)) {
      history.replace({
        pathname: `/product/session/selection/0`,
        search: location.search,
      });
    }

    louiseDispatch(setSelectionData(selectionDataVar));
  };

  return (
    <Content>
      {isLoading || isAffiliationsLoading || isBrokerAffiliationsLoading ? (
        <LoadingOverlay />
      ) : (
        <StyledSelectionContainer
          nextSelection={nextSelection}
          previousSelection={previousSelection}
          isLoading={isLoading}
          isSessionActive={sessionActive}
        />
      )}
    </Content>
  );
};

const StyledSelectionContainer = styled(SelectionContainer)`
  background: white;

  ${mq.laptop} {
    background-color: #fdfcff;
  }
`;

const Content = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

export default SelectionView;
